import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Button,
  ControlGroup,
  defaultColors,
  Dialog,
  EmptyInputGroup,
  FormField,
  Grid,
  Heading,
  IDialogProps,
  InputGroup,
  majorScale,
  NumberInput,
  TextArea,
} from '../../npaper';
import { DateInput } from '../../npaper/DateInput';
import { FormattedUnit } from '../../npaper/FormattedUnit';
import { UsersInput } from '../../npaper/UserSelect';
import {
  ITask,
  ITaskSnapshotIn,
  ProductType,
  Unit,
  useStores,
} from '../../stores';
import { CompanySelect } from '../companies/CompanySelect';
import { ContactsInput } from '../contacts/ContactSelect';
import { ProductSelect } from '../products/ProductSelect';

export interface ITaskDialogProps
  extends Omit<Omit<IDialogProps, 'title'>, 'onClose'> {
  onClose: (task?: ITask) => void;
}

export const TaskDialog: React.FC<ITaskDialogProps> = ({ ...props }) => {
  const { taskStore } = useStores();
  const { register, control, watch, handleSubmit, formState, errors } =
    useForm<ITaskSnapshotIn>();

  const onSubmit = async (values: ITaskSnapshotIn) => {
    const task = await taskStore.createItem(values);
    props.onClose(task);
  };

  return (
    <Dialog
      {...props}
      title="Opret opgave"
      actions={
        <>
          <Button
            onClick={() => props.onClose()}
            disabled={formState.isSubmitting}
          >
            Annuller
          </Button>
          <Button
            primary
            onClick={handleSubmit(onSubmit)}
            isLoading={formState.isSubmitting}
          >
            Opret opgave
          </Button>
        </>
      }
    >
      <Grid padding={majorScale(2)}>
        <Heading size={300} className="col-span-12">
          Generelt
        </Heading>

        <FormField
          className="col-span-9"
          label="Serviceydelse"
          inline={true}
          isInvalid={!!errors.service}
          hint={errors.service && errors.service.message}
        >
          <Controller
            control={control}
            name="service"
            rules={{
              required: 'Du skal vælge en serviceydelse for din opgave',
            }}
            defaultValue={null}
            render={({ onChange, onBlur, value }) => (
              <ProductSelect
                filter={{ type: ProductType.SERVICE }}
                onSelect={(product) => onChange(product.id)}
                placeholder="Vælg serviceydelse"
                selected={value}
              />
            )}
          />
        </FormField>

        <FormField
          className="col-span-9"
          label="Titel"
          inline={true}
          isInvalid={!!errors.title}
          hint={errors.title && errors.title.message}
        >
          <InputGroup
            ref={register({ required: 'Du skal give din opgave en titel' })}
            name="title"
            placeholder="Opgavens titel"
            isInvalid={!!errors.title}
          />
        </FormField>

        <FormField
          className="col-span-9"
          label="Kunde"
          inline={true}
          isInvalid={!!errors.customer}
          hint={
            (errors.customer && errors.customer.message) ||
            'Vælg den kunde som tilbudet går til'
          }
        >
          <Controller
            control={control}
            name="customer"
            defaultValue={null}
            rules={{ required: 'Du skal vælge en kunde' }}
            render={({ onChange, value }) => (
              <CompanySelect
                onSelect={(company) => onChange(company.id)}
                selected={value}
              />
            )}
          />
        </FormField>
      </Grid>
      <Grid
        padding={majorScale(2)}
        borderTop={`2px solid ${defaultColors.gray[100]}`}
      >
        <Heading size={300} className="col-span-12">
          Detaljer
        </Heading>

        <FormField className="col-span-9" label="Beskrivelse" inline={true}>
          <TextArea
            ref={register}
            name="description"
            maxLength={500}
            placeholder="En kort beskrivelse af opgaven"
          />
        </FormField>

        <FormField className="col-span-9" label="Tidsfrist" inline={true}>
          <Controller
            control={control}
            name="dueDate"
            defaultValue={null}
            render={({ onChange, onBlur, value }) => (
              <DateInput
                placeholder="Angiv deadline"
                value={value}
                onChange={(date) => onChange(date)}
              />
            )}
          />
        </FormField>

        <FormField className="col-span-9" label="Budget" inline={true}>
          <ControlGroup>
            <Controller
              control={control}
              name="budget"
              defaultValue={null}
              render={({ onChange, value, onBlur }) => (
                <NumberInput
                  placeholder="Angiv budget"
                  minimumFractionDigits={0}
                  maximumFractionDigits={2}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              )}
            />
            <EmptyInputGroup>
              <FormattedUnit unit={Unit.HOUR} unitDisplay="long" />
            </EmptyInputGroup>
          </ControlGroup>
        </FormField>

        <FormField
          className="col-span-9"
          label="Kontaktpersoner"
          inline={true}
          hint="Tilføj én eller flere kontaktpersoner til opgaven"
        >
          <Controller
            control={control}
            name="contacts"
            defaultValue={[]}
            render={({ onChange, value, onBlur }) => (
              <ContactsInput
                onBlur={onBlur}
                value={value}
                filter={{ company: watch('customer') }}
                onChange={(contacts) => {
                  onChange(contacts);
                }}
              />
            )}
          />
        </FormField>

        <FormField
          className="col-span-9"
          label="Ansvarlig"
          inline={true}
          hint="Vælg én fra dit team som er ansvarlig af salget"
        >
          <Controller
            control={control}
            name="responsible"
            defaultValue={[]}
            render={({ onChange, value, onBlur }) => (
              <UsersInput
                onBlur={onBlur}
                value={value}
                onChange={(users) => {
                  onChange(users.map((c) => c.id));
                }}
              />
            )}
          />
        </FormField>
      </Grid>
    </Dialog>
  );
};
